import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate } from 'react-router'
import { api } from '../api/client.js'
import { useApi } from '../api/useApi.js'
import { useAuth } from '../auth.jsx'
import { ClipCard, ClipModal, RankCard } from '../components/Clips.jsx'
import SocialLinks from '../components/SocialLinks.jsx'
import { pickI18n } from '../i18n'
import { money } from '../lib/money.js'
import { Loading } from './states.jsx'

const card = 'flex flex-col gap-3 rounded-3xl border border-line bg-surface p-6'
const h2 = 'text-sm font-extrabold tracking-[1.2px] text-subtle uppercase'

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 py-1.5 font-bold">
      {label}
      <input type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} className="size-5 cursor-pointer accent-teal" />
    </label>
  )
}

function PublicName({ profile, onSaved }) {
  const { t } = useTranslation()
  const [name, setName] = useState(profile.display_name ?? '')
  const [saved, setSaved] = useState(false)
  const save = async (body) => {
    await api.updateMe(body)
    onSaved()
    setSaved(true)
  }
  return (
    <section className={card}>
      <h2 className={h2}>{t('leaderboard.title')}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          save({ display_name: name.trim() || null })
        }}
        className="flex flex-col gap-2"
      >
        <label className="flex flex-col gap-1.5 text-sm font-extrabold">
          {t('leaderboard.publicName')}
          <span className="flex gap-2">
            <input
              maxLength={40}
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setSaved(false)
              }}
              placeholder={t('leaderboard.anonymous')}
              className="h-11 flex-1 rounded-xl border-[1.5px] border-edge bg-surface px-3 font-medium text-ink outline-none focus:border-teal"
            />
            <button type="submit" className="cursor-pointer rounded-xl bg-teal px-4 font-extrabold text-on-teal">
              {saved ? '✓' : t('leaderboard.save')}
            </button>
          </span>
        </label>
      </form>
      <Toggle label={t('leaderboard.hideMe')} checked={profile.leaderboard_opt_out} onChange={(v) => save({ leaderboard_opt_out: v })} />
      <Link to="/leaderboard" className="font-extrabold text-teal no-underline">{t('leaderboard.open')} →</Link>
    </section>
  )
}

function TelegramCard() {
  const { t } = useTranslation()
  const tg = useApi(() => api.telegram())
  if (!tg.data?.enabled) return null
  const connect = async () => {
    const { url } = await api.linkTelegram()
    window.open(url, '_blank', 'noopener')
    setTimeout(tg.reload, 15000) // after /start in Telegram
  }
  return (
    <section className={card}>
      <h2 className={h2}>Telegram</h2>
      <p className="m-0 text-muted">{t('telegram.lead')}</p>
      {tg.data.linked ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-extrabold text-teal">✓ {t('telegram.linked')}</span>
          <button type="button" onClick={() => api.unlinkTelegram().then(tg.reload)} className="cursor-pointer text-sm font-bold text-subtle underline">
            {t('telegram.unlink')}
          </button>
        </div>
      ) : (
        <button type="button" onClick={connect} className="cursor-pointer self-start rounded-full bg-[#229ED9] px-5 py-2.5 font-extrabold text-white">
          {t('telegram.connect')}
        </button>
      )}
    </section>
  )
}

function MyDonations() {
  const { t, i18n } = useTranslation()
  const { data } = useApi(() => api.myDonations())
  const [error, setError] = useState(null)
  const paid = (data ?? []).filter((d) => d.status === 'paid')
  if (!paid.length) return null
  const total = paid.reduce((sum, d) => sum + d.amount_cents, 0)
  const portal = async () => {
    try {
      window.location.assign((await api.billingPortal()).url)
    } catch (e) {
      setError(e.message)
    }
  }
  return (
    <section className={card}>
      <h2 className={h2}>{t('donate.mine')}</h2>
      <p className="m-0 text-lg">
        {t('donate.mineTotal', { amount: money(total, paid[0].currency, i18n.resolvedLanguage), count: paid.reduce((n, d) => n + d.meals, 0) })}
      </p>
      {paid.some((d) => d.kind === 'monthly') ? (
        <button type="button" onClick={portal} className="cursor-pointer self-start rounded-full border-[1.5px] border-edge px-5 py-2.5 font-extrabold hover:border-ink">
          {t('donate.manageMonthly')}
        </button>
      ) : null}
      {error ? <p role="alert" className="m-0 font-bold text-live">{error}</p> : null}
    </section>
  )
}

/** Signed-in only: redirects to the login page and back. */
export function RequireUser({ children }) {
  const { user, ready } = useAuth()
  if (!ready) return null
  if (!user) return <Navigate to="/login" state={{ from: window.location.pathname }} replace />
  return children
}

export default function ProfilePage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const { user, profile, refreshProfile, deleteAccount } = useAuth()
  const navigate = useNavigate()
  const [openClip, setOpenClip] = useState(null)
  const [error, setError] = useState(null)
  const follows = useApi(() => api.follows(), [user.uid])
  const feeders = useApi(() => api.feeders())
  const cats = useApi(() => api.cats())
  const clips = useApi(() => api.myClips(), [user.uid])

  if (!profile) return <Loading />

  const setPref = async (key, value) => {
    setError(null)
    try {
      await api.updateMe({ [key]: value })
      refreshProfile()
    } catch (e) {
      setError(e.message)
    }
  }

  const items = (follows.data ?? [])
    .map((f) => {
      if (f.feeder_id) {
        const fd = feeders.data?.find((x) => x.id === f.feeder_id)
        return fd && { key: `f${f.id}`, to: `/feeders/${fd.slug}`, label: `📍 ${pickI18n(fd.name, lang)}` }
      }
      const c = cats.data?.find((x) => x.id === f.cat_id)
      return c && { key: `c${f.id}`, to: '/cats', label: `🐱 ${c.name}` }
    })
    .filter(Boolean)

  const remove = async () => {
    if (!window.confirm(t('cabinet.deleteConfirm'))) return
    try {
      await deleteAccount()
      navigate('/', { replace: true })
    } catch (e) {
      setError(e.message)
    }
  }

  return (
    <main className="mx-auto flex max-w-5xl flex-col gap-7 px-4 pt-10 pb-[72px] sm:px-8">
      <div className="flex flex-col gap-1">
        <h1 className="text-[clamp(32px,4vw,44px)] font-black tracking-[-1px]">{t('cabinet.title')}</h1>
        <p className="text-lg text-muted">
          {user.name}
          {user.email && user.email !== user.name ? ` · ${user.email}` : ''}
        </p>
      </div>

      <RankCard profile={profile} />

      <section className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="text-[28px] font-black tracking-[-0.6px]">{t('cabinet.videos')}</h2>
          <Link to="/videos" className="font-extrabold text-teal no-underline hover:text-teal-dark">
            {t('cabinet.allVideos')} →
          </Link>
        </div>
        {clips.data?.length ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(260px,100%),1fr))] gap-5">
            {clips.data.slice(0, 6).map((c) => (
              <ClipCard key={c.id} clip={c} onOpen={(x) => setOpenClip(x.id)} />
            ))}
          </div>
        ) : clips.loading ? null : (
          <div className="flex flex-col items-start gap-3 rounded-3xl bg-paper p-6">
            <span className="text-lg font-extrabold">🎬 {t('cabinet.noVideosTitle')}</span>
            <span className="text-muted">{t('cabinet.noVideosText')}</span>
            <Link to="/cats" className="rounded-full bg-teal px-5 py-2.5 font-extrabold text-on-teal no-underline">
              {t('cabinet.findCats')}
            </Link>
          </div>
        )}
      </section>

      <div className="grid gap-6 md:grid-cols-2">
        <section className={card}>
          <h2 className={h2}>{t('cabinet.follows')}</h2>
          {items.length ? (
            <ul className="m-0 flex list-none flex-col p-0">
              {items.map((i) => (
                <li key={i.key} className="border-b border-line last:border-0">
                  <Link to={i.to} className="flex justify-between py-3 font-bold text-ink no-underline hover:text-teal">
                    {i.label} <span aria-hidden="true">›</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 text-muted">{t('cabinet.noFollows')}</p>
          )}
        </section>

        <PublicName profile={profile} onSaved={refreshProfile} />

        <TelegramCard />

        <section className={card}>
          <h2 className={h2}>{t('cabinet.notifications')}</h2>
          <Toggle label={t('cabinet.notifyCat')} checked={profile.notify_cat_detected} onChange={(v) => setPref('notify_cat_detected', v)} />
          <Toggle label={t('cabinet.notifyFood')} checked={profile.notify_food_low} onChange={(v) => setPref('notify_food_low', v)} />
          <Toggle label={t('cabinet.notifyClips')} checked={profile.notify_clips} onChange={(v) => setPref('notify_clips', v)} />
          <Toggle label={t('cabinet.notifyDigest')} checked={profile.notify_digest} onChange={(v) => setPref('notify_digest', v)} />
          <p className="m-0 text-sm text-subtle">{t('cabinet.pushHint')}</p>
        </section>
      </div>

      <MyDonations />

      {error ? <p role="alert" className="font-bold text-live">{error}</p> : null}

      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-line pt-6">
        <SocialLinks title />
        <span className="flex flex-wrap gap-3">
          <Link to="/contact" className="rounded-full border-[1.5px] border-edge px-5 py-2.5 font-extrabold text-ink no-underline hover:border-ink">
            {t('contact.title')}
          </Link>
          <button type="button" onClick={remove} className="cursor-pointer rounded-full border-[1.5px] border-live px-5 py-2.5 font-extrabold text-live">
            {t('cabinet.deleteAccount')}
          </button>
        </span>
      </div>

      {openClip ? <ClipModal clipId={openClip} onClose={() => setOpenClip(null)} /> : null}
    </main>
  )
}
